import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { PERMISSIONS, type CustomerDocumentView } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { toDisplayMessage } from '../../../lib/error-message';

import styles from './AdminPortal.module.css';

export function CustomerDocumentsPanel({
  customerId,
  variant = 'all',
  customerLabel,
}: {
  customerId: string;
  variant?: 'all' | 'identity';
  customerLabel?: string;
}): React.JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<{
    url: string;
    mimeType: string;
    label: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const mayRead = can(PERMISSIONS.CUSTOMER_DOCUMENTS_READ);
  const documents = useQuery({
    queryKey: ['admin', 'customers', customerId, 'documents'],
    queryFn: () => customersApi.listAdminDocuments(customerId),
    enabled: mayRead,
  });

  useEffect(() => {
    return () => {
      if (preview !== null) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const review = useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: 'VERIFIED' | 'REJECTED';
      reason?: string;
    }) => customersApi.reviewAdminDocument(id, { status, reason }),
    onSuccess: async () => {
      setRejectingId(null);
      setRejectionReason('');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'customers', customerId, 'documents'],
      });
    },
  });

  const visibleDocuments =
    variant === 'identity'
      ? (documents.data ?? []).filter(
          (document) =>
            document.documentType === 'NIN_SLIP' && document.reviewStatus !== 'SUPERSEDED',
        )
      : (documents.data ?? []);

  const openPreview = async (document: CustomerDocumentView): Promise<void> => {
    try {
      const blob = await customersApi.previewDocument(document.id, true);
      if (preview !== null) URL.revokeObjectURL(preview.url);
      setPreview({
        url: URL.createObjectURL(blob),
        mimeType: document.mimeType,
        label: `${document.documentType.replace(/_/g, ' ')} preview`,
      });
      setActionError(null);
    } catch (error: unknown) {
      setActionError(toDisplayMessage(error));
    }
  };

  const download = async (document: CustomerDocumentView): Promise<void> => {
    try {
      const blob = await customersApi.downloadDocument(document.id, true);
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.originalFilename;
      anchor.click();
      URL.revokeObjectURL(url);
      setActionError(null);
    } catch (error: unknown) {
      setActionError(toDisplayMessage(error));
    }
  };

  return (
    <section
      className={`${styles.card} ${variant === 'identity' ? styles.identityCard : ''}`}
      aria-labelledby="customer-documents-heading"
    >
      <h2 className={styles.cardTitle} id="customer-documents-heading">
        {variant === 'identity' ? 'Identity verification' : 'Private documents'}
      </h2>
      <p className={styles.pageSubtitle}>
        {variant === 'identity'
          ? `${customerLabel ?? 'This customer'} · current NIN verification evidence for this application.`
          : 'Sensitive identity and contract evidence. Every preview, download and review is audited.'}
      </p>

      {!mayRead && (
        <p className={styles.empty}>Your role is not authorized to view private documents.</p>
      )}
      {documents.isPending && mayRead && <PageSpinner label="Loading private documents" />}
      {documents.isError && <Alert tone="error">{toDisplayMessage(documents.error)}</Alert>}
      {actionError !== null && <Alert tone="error">{actionError}</Alert>}
      {review.isError && <Alert tone="error">{toDisplayMessage(review.error)}</Alert>}
      {documents.isSuccess && visibleDocuments.length === 0 && (
        <p className={styles.empty}>
          {variant === 'identity'
            ? 'No current NIN slip is available. Final approval is blocked.'
            : 'No private document has been uploaded.'}
        </p>
      )}

      {documents.isSuccess && visibleDocuments.length > 0 && (
        <div className={styles.documentGrid}>
          {visibleDocuments.map((document) => (
            <article className={styles.documentCard} key={document.id}>
              <div className={styles.documentCardHeader}>
                <div>
                  <h3>{document.documentType.replace(/_/g, ' ')}</h3>
                  <p>{document.originalFilename}</p>
                </div>
                <StatusBadge status={document.reviewStatus} />
              </div>
              <dl className={styles.definitionList}>
                <dt>Uploaded</dt>
                <dd>{new Date(document.uploadedAt).toLocaleString()}</dd>
                <dt>Type</dt>
                <dd>{document.mimeType}</dd>
                <dt>Size</dt>
                <dd>{(document.byteSize / 1_000_000).toFixed(2)} MB</dd>
                <dt>Security scan</dt>
                <dd>{document.scanStatus.replace(/_/g, ' ')}</dd>
                <dt>Contract evidence</dt>
                <dd>{document.boundToAcceptedContract ? 'Bound and retained' : 'Not bound'}</dd>
                {document.reviewedAt !== null && (
                  <>
                    <dt>Reviewed</dt>
                    <dd>{new Date(document.reviewedAt).toLocaleString()}</dd>
                  </>
                )}
                {document.reviewedByUserId !== null && (
                  <>
                    <dt>Reviewer account</dt>
                    <dd>{document.reviewedByUserId}</dd>
                  </>
                )}
                {document.rejectionReason !== null && (
                  <>
                    <dt>Safe rejection reason</dt>
                    <dd>{document.rejectionReason}</dd>
                  </>
                )}
              </dl>
              <div className={styles.actions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void openPreview(document)}
                >
                  Preview
                </Button>
                {can(PERMISSIONS.CUSTOMER_DOCUMENTS_DOWNLOAD) && (
                  <Button type="button" variant="secondary" onClick={() => void download(document)}>
                    Download
                  </Button>
                )}
                {document.documentType === 'NIN_SLIP' &&
                  document.reviewStatus !== 'SUPERSEDED' &&
                  can(PERMISSIONS.CUSTOMER_DOCUMENTS_REVIEW) && (
                    <>
                      <Button
                        type="button"
                        disabled={review.isPending}
                        onClick={() => review.mutate({ id: document.id, status: 'VERIFIED' })}
                      >
                        Mark verified
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setRejectingId(document.id)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
              </div>
              {rejectingId === document.id && (
                <div className={styles.reviewReason}>
                  <label>
                    <span>Customer-safe rejection reason</span>
                    <textarea
                      value={rejectionReason}
                      maxLength={500}
                      onChange={(event) => setRejectionReason(event.target.value)}
                    />
                  </label>
                  <Button
                    type="button"
                    disabled={rejectionReason.trim() === '' || review.isPending}
                    onClick={() =>
                      review.mutate({
                        id: document.id,
                        status: 'REJECTED',
                        reason: rejectionReason.trim(),
                      })
                    }
                  >
                    Confirm rejection
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {preview !== null && (
        <div
          className={styles.documentModal}
          role="dialog"
          aria-modal="true"
          aria-label={preview.label}
        >
          <div className={styles.documentModalPanel}>
            <div className={styles.documentModalHeader}>
              <h3>{preview.label}</h3>
              <Button type="button" variant="secondary" onClick={() => setPreview(null)}>
                Close
              </Button>
            </div>
            {preview.mimeType === 'application/pdf' ? (
              <iframe
                src={preview.url}
                title={preview.label}
                sandbox=""
                referrerPolicy="no-referrer"
              />
            ) : (
              <img src={preview.url} alt={preview.label} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
